import { EventType } from 'constants/'
import { useEffect, useMemo, useRef } from 'react'
import type { CareMachineEventData } from 'types'
import { useCurrentUser } from '../useCurrentUser'
import { useCustomer } from '../useCustomer'
import { useEventTracking } from '../useEventTracking'
import { useMixpanel } from '../useMixpanel'
import { useUser } from '../useUser'

interface Props {
  eventType: EventType
  /**
   * True while the caller is still resolving the opportunity. Holds the event back rather than
   * letting it ship without the two opportunity ids — the dedupe below is per customer, so a
   * truncated event would never be corrected. Leave unset when there is nothing to wait for.
   */
  isOpportunityPending?: boolean
  isShown: boolean
  /**
   * The opportunity ids are passed in rather than read from `useOpportunity`, because the Your
   * Appointments view renders Care Machine outside any `OpportunityProvider`.
   */
  opportunityGid?: string
  opportunitySalesforceId?: string
}

interface CareMachineImpression {
  data: CareMachineEventData
}

/**
 * Reports a Care Machine surface being shown, once per customer, carrying only ids/gids —
 * never names or emails. Returns the assembled payload so a caller can attach it to its own
 * events (a click, say) without duplicating the plumbing.
 */
const useCareMachineImpression = ({
  eventType,
  isOpportunityPending,
  isShown,
  opportunityGid,
  opportunitySalesforceId,
}: Props): CareMachineImpression => {
  const { track } = useEventTracking()
  const { mixpanel } = useMixpanel()
  const { accountId } = useUser()
  const { data: customer } = useCustomer()
  const { data: currentUser, isLoading: isCurrentUserLoading } = useCurrentUser()

  const customerGid = customer?.gid
  const partnerEmployeeGid = currentUser?.relatedEntityGids?.partnerEmployeeGid ?? undefined

  const data: CareMachineEventData = useMemo(
    () => ({ accountId, customerGid, opportunityGid, opportunitySalesforceId, partnerEmployeeGid }),
    [accountId, customerGid, opportunityGid, opportunitySalesforceId, partnerEmployeeGid]
  )

  // The impression is worth nothing without the identity it attributes, so hold it until Mixpanel
  // is initialised and both identity sources have settled. A failed or absent source still releases
  // it — the event goes out with that field missing rather than being dropped.
  const isReadyToTrack = Boolean(mixpanel) && !isCurrentUserLoading && !isOpportunityPending

  // Deduped by customer gid rather than a plain "already fired" flag, so moving between customers
  // within the same session reports a new impression but re-renders do not.
  // The `undefined` seed doubles as the "customer not loaded yet" guard: it matches the not-yet-known
  // gid and holds the impression back, so a customer is not reported once without a gid and again with
  // one. Keep the seed as `undefined` — a sentinel like `null` would reintroduce that double report.
  const lastShownCustomerGid = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isShown || !isReadyToTrack) return
    if (lastShownCustomerGid.current === data.customerGid) return

    track(eventType, { data })

    // Advanced only after a successful send, so a throw does not suppress this customer's
    // impression for the rest of the session.
    lastShownCustomerGid.current = data.customerGid
  }, [data, eventType, isReadyToTrack, isShown, track])

  return { data }
}

export { useCareMachineImpression }
